import config from "config";

export interface CanvaFile {
  designId: string;
  designTitle: string;
  exportUrl: string;
}

export const CANVA_CLOSE_MESSAGE = "Canva design flow closed";
const CANVA_AUTH_FAILURE_MESSAGE = "Canva authentication failed";
const CANVA_KNOWN_ERROR_CODES = [
  "ERR_AUTH_DOMAIN_POPUP_CLOSED",
  "ERR_FINDER_AUTH_FAILED",
] as const;

const CANVA_SCRIPT_SRC = "https://sdk.canva.com/designbutton/v2/api.js";
const CANVA_SCRIPT_ID = "canva-design-button-sdk";
const CANVA_READY_TIMEOUT_MS = 10_000;
const CANVA_READY_POLL_INTERVAL_MS = 100;

let canvaReadyPromise: Promise<void> | null = null;

interface CanvaApi {
  createDesign(config: {
    design: {
      type: string;
    };
    editor: {
      publishLabel: string;
      fileType: string;
    };
    onDesignPublish(file: CanvaFile): void;
    onDesignClose?(): void;
  }): void;
}

let canvaApiPromise: Promise<CanvaApi> | null = null;

type CanvaWindow = Window & {
  Canva?: {
    DesignButton?: {
      initialize(config: { apiKey: string }): Promise<CanvaApi>;
    };
  };
  CanvaApi?: CanvaApi;
};

function getCanvaWindow() {
  return window as CanvaWindow;
}

const hasCanvaInitializer = () =>
  typeof getCanvaWindow().Canva?.DesignButton?.initialize === "function";

const getOrCreateCanvaScript = (): HTMLScriptElement => {
  const existingScript =
    document.querySelector<HTMLScriptElement>(`script#${CANVA_SCRIPT_ID}`) ??
    document.querySelector<HTMLScriptElement>(
      `script[src="${CANVA_SCRIPT_SRC}"]`
    );

  if (existingScript) {
    return existingScript;
  }

  const script = document.createElement("script");
  script.id = CANVA_SCRIPT_ID;
  script.src = CANVA_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.append(script);

  return script;
};

const waitForCanvaReady = () =>
  new Promise<void>((resolve, reject) => {
    if (hasCanvaInitializer()) {
      resolve();
      return;
    }

    const start = Date.now();
    const interval = window.setInterval(() => {
      if (hasCanvaInitializer()) {
        window.clearInterval(interval);
        resolve();
        return;
      }

      if (Date.now() - start >= CANVA_READY_TIMEOUT_MS) {
        window.clearInterval(interval);
        reject(new Error("Canva picker is unavailable"));
      }
    }, CANVA_READY_POLL_INTERVAL_MS);
  });

const ensureCanvaReady = async () => {
  if (hasCanvaInitializer()) {
    return;
  }

  if (!canvaReadyPromise) {
    canvaReadyPromise = (async () => {
      getOrCreateCanvaScript();
      await waitForCanvaReady();
    })().catch((error) => {
      canvaReadyPromise = null;
      throw error;
    });
  }

  await canvaReadyPromise;
};

async function getCanvaApi() {
  await ensureCanvaReady();

  const canvaWindow = getCanvaWindow();
  if (canvaWindow.CanvaApi) {
    return canvaWindow.CanvaApi;
  }

  if (!canvaApiPromise) {
    const initialize = canvaWindow.Canva?.DesignButton?.initialize;
    if (!initialize) {
      throw new Error("Canva picker is unavailable");
    }

    canvaApiPromise = initialize({
      apiKey: config.canvaApiKey,
    })
      .then((canvaApi) => {
        canvaWindow.CanvaApi = canvaApi;
        return canvaWindow.CanvaApi;
      })
      .catch((error) => {
        canvaApiPromise = null;
        throw error;
      });
  }

  const canvaApi = await canvaApiPromise;
  if (!canvaApi) {
    throw new Error("Canva picker is unavailable");
  }

  return canvaApi;
}

function getCanvaErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }

    if ("reason" in error && typeof error.reason === "string") {
      return error.reason;
    }
  }

  return "";
}

function isKnownCanvaError(error: unknown) {
  const message = getCanvaErrorMessage(error);

  return CANVA_KNOWN_ERROR_CODES.some((code) => message.includes(code));
}

export async function selectFileFromCanva(type?: string): Promise<CanvaFile> {
  const canvaApi = await getCanvaApi();

  return new Promise((resolve, reject) => {
    let isSettled = false;

    const cleanup = () => {
      window.removeEventListener("error", handleWindowError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
        true
      );
    };

    const resolveOnce = (file: CanvaFile) => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      cleanup();
      resolve(file);
    };

    const rejectOnce = (error: Error) => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      cleanup();
      reject(error);
    };

    const handleWindowError = (event: ErrorEvent) => {
      const message = getCanvaErrorMessage(event.error ?? event.message);
      const isScriptError = event.message === "Script error.";
      const isCanvaOrigin =
        typeof event.filename === "string" &&
        (event.filename.includes("canva.com") ||
          event.filename.includes("sdk.canva.com"));

      if (!isKnownCanvaError(message) && !(isScriptError && isCanvaOrigin)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      rejectOnce(new Error(CANVA_AUTH_FAILURE_MESSAGE));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isKnownCanvaError(event.reason)) {
        return;
      }

      event.preventDefault();
      rejectOnce(new Error(CANVA_AUTH_FAILURE_MESSAGE));
    };

    window.addEventListener("error", handleWindowError, true);
    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection,
      true
    );

    canvaApi.createDesign({
      design: {
        type: type || "SocialMedia",
      },
      editor: {
        publishLabel: "Export to Sked",
        fileType: "jpeg",
      },
      onDesignPublish: (file) => resolveOnce(file),
      onDesignClose: () => rejectOnce(new Error(CANVA_CLOSE_MESSAGE)),
      // onDesignPublish: async (result: any) => {
      //   resolve(result)
      //   const downloads = fetchBlobs([
      //     {
      //       link: result.exportUrl,
      //       name: result.designTitle,
      //     },
      //   ]);
      //   try {
      //     const blobs = await Promise.all(downloads);
      //     resolve(blobs);
      //   } catch (e) {
      //     console.error(e);
      //     reject({
      //       error: "Failed to download files from Canva",
      //     });
      //   }
      // },
    });
  });
}
